import { NgTemplateOutlet } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { FilterBase } from 'src/app/shared/grid/filter-base';
import { OptionModel } from './option.model';
import { GRID_MATERIAL_PROVIDERS } from 'src/app/shared/grid/grid-material-control-defaults.const';

@Component({
    selector: 'grid-option-list-filter',
    template: ` @if (showOptionSearch) {
            <mat-form-field class="filter-option-search">
                <mat-label>{{ title || 'Search' }}</mat-label>
                <input
                    matInput
                    [placeholder]="title || 'Search'"
                    [(ngModel)]="filterSearch" />
            </mat-form-field>
        }
        <mat-selection-list
            #gridSelectionList
            (selectionChange)="addOption($event)">
            @for (option of getFilteredOptions(); track option; let i = $index) {
                <mat-list-option
                    color="primary"
                    [value]="option.id"
                    [attr.title]="option.name"
                    [selected]="selectedOptions?.includes(option.id)">
                    @if (optionTemplate) {
                        <ng-container
                            [ngTemplateOutlet]="optionTemplate"
                            [ngTemplateOutletContext]="{ $implicit: option }"></ng-container>
                    }
                    @if (!optionTemplate) {
                        <span>{{ option[optionFieldName] }}</span>
                    }
                </mat-list-option>
            }
        </mat-selection-list>`,
    styles: ['.filter-option-search { margin: 5px 0 -1.25em 1em; }', 'mat-selection-list { margin-top: 10px; }'],
    providers: [...GRID_MATERIAL_PROVIDERS, { provide: FilterBase, useExisting: GridOptionListFilterComponent }],
    standalone: true,
    imports: [NgTemplateOutlet, FormsModule, MatFormFieldModule, MatInputModule, MatListModule]
})
export class GridOptionListFilterComponent extends FilterBase implements OnChanges {
    @ViewChild(MatSelectionList) gridSelectionList: MatSelectionList;
    selectedOptions: any[];
    filterSearch: string = '';

    @Input() options: any[] = [];
    @Input() optionTemplate: TemplateRef<any> = null;
    @Input() singleSelection?: boolean;
    @Input() convertFilterFormat?: boolean = false;
    @Input() optionFieldName?: string = 'name';
    @Input() showOptionSearch?: boolean = false;
    @Input() title = 'Search';
    @Output() filterValueChanged = new EventEmitter<CompositeFilterDescriptor>();

    ngOnChanges(changes: SimpleChanges) {
        if (changes['filter']) {
            let option = this.getLatestFilterValue();

            if (option && !Array.isArray(option) && !this.convertFilterFormat) {
                if (typeof option === 'boolean') {
                    option = Array.from([option === true]);
                } else {
                    option = Array.from(option);
                }
            }

            if (this.convertFilterFormat && option !== null && !Array.isArray(option)) {
                option = Array.from([option === 'true']);
            }
            this.selectedOptions = option;
        }
    }

    addOption(item: MatSelectionListChange) {
        // handle multiple selects
        if (!this.singleSelection) {
            this.selectedOptions = this.selectedOptions || [];
            for (const option of item.options) {
                const ix = this.selectedOptions.indexOf(option.value);
                if (ix >= 0) {
                    this.selectedOptions = this.selectedOptions.filter((_, i) => i !== ix);
                } else {
                    this.selectedOptions = [...this.selectedOptions, option.value];
                }
            }
        } else {
            // handle single select
            this.selectedOptions = [];
            if (item.options.length && item.options[0].selected) {
                this.selectedOptions.push(item.options[0].value);
            }
        }
        this.updateSelectedOptions();
    }

    updateSelectedOptions(selectedOptions?: OptionModel[]) {
        if (Array.isArray(selectedOptions)) {
            this.selectedOptions = selectedOptions;
        }

        this.trimFilterByField(this.filter, this.field);
        this.updateFilter({
            field: this.field,
            operator: 'eq',
            value: this.convertFilterFormat ? this.selectedOptions[0].toString() : this.selectedOptions
        });

        this.filterValueChanged.emit(this.filter);
    }

    getFilteredOptions() {
        const isVisible = (o) => o.visible === undefined || o.visible;
        const matchesSearch = (o) => o[this.optionFieldName]?.toLowerCase().startsWith(search);

        if (!this.showOptionSearch) {
            return this.options?.filter((o) => isVisible(o));
        }

        const search = this.filterSearch?.toLowerCase();
        const filteredOptions = search
            ? this.options.filter((o) => isVisible(o) && matchesSearch(o))
            : (this.options ?? []); // options can be null if args is set to the grid component before the options are fetched
        return this.selectedOptions?.length
            ? filteredOptions.sort((a, b) => {
                  const aIsSelected = this.selectedOptions?.includes(a.id) || false;
                  const bIsSelected = this.selectedOptions?.includes(b.id) || false;

                  if (aIsSelected === bIsSelected) {
                      return a.name.localeCompare(b.name) ? 1 : -1;
                  } else {
                      return aIsSelected ? -1 : 1;
                  }
              })
            : filteredOptions;
    }
}
